import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { clearUserSessionActivity, SESSION_IDLE_TIMEOUT_MS, USER_ACTIVITY_EVENT, USER_ACTIVITY_KEY } from '../utils/sessionActivity';
import IdleWarningDialog from './IdleWarningDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];
const WARNING_WINDOW_MS = 60 * 1000;

const hasSignedInUser = () => Boolean(localStorage.getItem('user_data') || localStorage.getItem('investor_data'));

const SessionInactivityProvider = ({ children }) => {
  const [sessionVersion, setSessionVersion] = useState(0);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const expiringRef = useRef(false);
  const lastRecordedRef = useRef(0);
  const warningOpenRef = useRef(false);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    warningOpenRef.current = false;
  }, []);

  const expireSession = useCallback(async () => {
    if (expiringRef.current || !hasSignedInUser()) return;
    expiringRef.current = true;
    sessionStorage.setItem('dfl_session_expired', '1');
    localStorage.removeItem('user_data');
    localStorage.removeItem('investor_data');
    clearUserSessionActivity();
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // The browser state is already cleared; a network failure must not retain a stale session.
    }
    window.location.assign('/auth?reason=inactive');
  }, []);

  const showWarning = useCallback(() => {
    if (!hasSignedInUser() || expiringRef.current) return;
    const updateCountdown = () => {
      const lastActivity = Number(sessionStorage.getItem(USER_ACTIVITY_KEY)) || Date.now();
      setSecondsRemaining(Math.max(0, Math.ceil((lastActivity + SESSION_IDLE_TIMEOUT_MS - Date.now()) / 1000)));
    };
    updateCountdown();
    warningOpenRef.current = true;
    setWarningOpen(true);
    countdownTimerRef.current = window.setInterval(updateCountdown, 1000);
  }, []);

  const scheduleExpiry = useCallback(() => {
    clearTimers();
    if (!hasSignedInUser()) return;
    const lastActivity = Number(sessionStorage.getItem(USER_ACTIVITY_KEY)) || Date.now();
    const remaining = SESSION_IDLE_TIMEOUT_MS - (Date.now() - lastActivity);
    warningTimerRef.current = window.setTimeout(showWarning, Math.max(0, remaining - WARNING_WINDOW_MS));
    timerRef.current = window.setTimeout(expireSession, Math.max(0, remaining));
  }, [clearTimers, expireSession, showWarning]);

  useEffect(() => {
    const recordActivity = () => {
      if (!hasSignedInUser() || expiringRef.current) return;
      if (warningOpenRef.current) return;
      const now = Date.now();
      if (now - lastRecordedRef.current < 1000) return;
      lastRecordedRef.current = now;
      sessionStorage.setItem(USER_ACTIVITY_KEY, String(now));
      setWarningOpen(false);
      scheduleExpiry();
    };
    const startSession = () => {
      expiringRef.current = false;
      lastRecordedRef.current = Date.now();
      sessionStorage.setItem(USER_ACTIVITY_KEY, String(lastRecordedRef.current));
      setSessionVersion((value) => value + 1);
    };

    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, recordActivity, { passive: true }));
    window.addEventListener(USER_ACTIVITY_EVENT, startSession);
    scheduleExpiry();

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, recordActivity));
      window.removeEventListener(USER_ACTIVITY_EVENT, startSession);
      clearTimers();
    };
  }, [clearTimers, scheduleExpiry, sessionVersion]);

  const staySignedIn = () => {
    lastRecordedRef.current = Date.now();
    sessionStorage.setItem(USER_ACTIVITY_KEY, String(lastRecordedRef.current));
    warningOpenRef.current = false;
    setWarningOpen(false);
    scheduleExpiry();
  };

  return <>{children}<IdleWarningDialog open={warningOpen} secondsRemaining={secondsRemaining} onStaySignedIn={staySignedIn} /></>;
};

export default SessionInactivityProvider;